import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Text, Title, VIPSO_RED } from '@vipso/shared';
import { resolveTenantDefaultLogoUrl } from '../../utils/resolveDisplayLogoUrl';
import { resolveTenantSlug } from '../../utils/tenant';

export function LoginLandingLayout({
  children,
  logoUrl,
  title,
  subtitle,
  primaryColor,
}: {
  children: ReactNode;
  logoUrl?: string | null;
  title?: string;
  subtitle?: string;
  /** Tenant/org brand primary; falls back to VIPSo red. */
  primaryColor?: string | null;
}) {
  const { t } = useTranslation();
  const logoSrc = logoUrl || resolveTenantDefaultLogoUrl(resolveTenantSlug());
  const resolvedTitle = title ?? t('auth.login');
  const resolvedSubtitle = subtitle ?? t('auth.signInSubtitle');
  const brand = primaryColor?.trim() || VIPSO_RED;

  return (
    <Box
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 'calc(32px + env(safe-area-inset-top, 0px))',
        paddingBottom: 'calc(32px + env(safe-area-inset-bottom, 0px))',
        paddingLeft: 'calc(16px + env(safe-area-inset-left, 0px))',
        paddingRight: 'calc(16px + env(safe-area-inset-right, 0px))',
        background: '#ffffff',
        // Expose brand for child button styles
        ['--login-brand' as string]: brand,
      }}
    >
      <Box style={{ width: '100%', maxWidth: 420 }}>
        <Stack align="center" gap="xs" mb="xl">
          <img
            src={logoSrc}
            alt=""
            style={{
              width: 150,
              height: 200,
              objectFit: 'contain',
              display: 'block',
            }}
          />
          <Title order={2} ta="center" c="navy.9" mt="md">
            {resolvedTitle}
          </Title>
          <Text size="xs" c="dimmed" ta="center">
            {resolvedSubtitle}
          </Text>
        </Stack>
        {children}
      </Box>
    </Box>
  );
}

export const loginPrimaryButtonStyle: CSSProperties = {
  marginTop: 20,
  width: '100%',
  borderRadius: 10,
  backgroundColor: 'var(--login-brand, #cc202e)',
};
